import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FiX, FiInfo, FiCalendar, FiMapPin, FiTrendingUp } from 'react-icons/fi';
import Button from '../ui/Button';
import { useQuotesBookings } from './QuotesBookingsContext';
import styles from './CreateQuoteDrawer.module.css';
import drawerStyles from './Drawer.module.css';

const modes = ['FTL', 'LTL'];

const createStop = () => ({ city: '', state: '', zip: '', date: '', time: '', notes: '' });

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute of [0, 30]) {
      options.push(`${hour.toString().padStart(2, '0')}:${minute === 0 ? '00' : '30'}`);
    }
  }
  return options;
};

const normalizeTime = (value) => {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  const colonIndex = trimmed.indexOf(':');
  let hours;
  let minutes;
  if (colonIndex !== -1) {
    const [h, m] = trimmed.split(':');
    hours = Number.parseInt(h, 10);
    minutes = Number.parseInt(m, 10);
  } else {
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length <= 2) {
      hours = Number.parseInt(digits || '0', 10);
      minutes = 0;
    } else if (digits.length === 3) {
      hours = Number.parseInt(digits.slice(0, 1), 10);
      minutes = Number.parseInt(digits.slice(1), 10);
    } else {
      hours = Number.parseInt(digits.slice(0, 2), 10);
      minutes = Number.parseInt(digits.slice(2, 4), 10);
    }
  }
  if (Number.isNaN(hours)) {
    hours = 0;
  }
  if (Number.isNaN(minutes)) {
    minutes = 0;
  }
  hours = Math.min(Math.max(hours, 0), 23);
  minutes = Math.min(Math.max(minutes, 0), 59);
  const normalizedMinutes = minutes < 15 ? 0 : minutes < 45 ? 30 : 30;
  return `${hours.toString().padStart(2, '0')}:${normalizedMinutes === 0 ? '00' : '30'}`;
};

const CreateQuoteDrawer = ({ open, onClose }) => {
  const { addQuote, trailerDefaults } = useQuotesBookings();
  const [mode, setMode] = useState('FTL');
  const [equipment, setEquipment] = useState('Dry Van');
  const [pickupCount, setPickupCount] = useState(1);
  const [dropCount, setDropCount] = useState(1);
  const [pickups, setPickups] = useState([createStop()]);
  const [drops, setDrops] = useState([createStop()]);
  const [notes, setNotes] = useState('');
  const [timeListId] = useState(() => `quote-times-${Math.random().toString(36).slice(2)}`);

  const availableEquipment = useMemo(() => Object.keys(trailerDefaults), [trailerDefaults]);
  const timeOptions = useMemo(generateTimeOptions, []);

  useEffect(() => {
    setPickups((prev) => {
      const next = [...prev];
      if (pickupCount > prev.length) {
        for (let i = prev.length; i < pickupCount; i += 1) {
          next.push(createStop());
        }
      } else if (pickupCount < prev.length) {
        next.length = pickupCount;
      }
      return next;
    });
  }, [pickupCount]);

  useEffect(() => {
    setDrops((prev) => {
      const next = [...prev];
      if (dropCount > prev.length) {
        for (let i = prev.length; i < dropCount; i += 1) {
          next.push(createStop());
        }
      } else if (dropCount < prev.length) {
        next.length = dropCount;
      }
      return next;
    });
  }, [dropCount]);

  if (!open) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const pickupDisplay = `${pickups[0].city || 'Origin'}, ${pickups[0].state || ''}`.trim();
    const dropDisplay = `${drops[0].city || 'Destination'}, ${drops[0].state || ''}`.trim();
    addQuote({
      mode,
      equipment,
      lane: `${pickupDisplay} → ${dropDisplay}`,
      pickupDate: pickups[0].date || 'Pending',
      deliveryDate: drops[0].date || 'Pending',
      rateEstimate: null,
      stops: pickupCount + dropCount,
      notes
    });
    onClose();
  };

  const handlePickupChange = (index, field, value, normalizeValue = false) => {
    setPickups((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: normalizeValue ? normalizeTime(value) : value };
      return next;
    });
  };

  const handleDropChange = (index, field, value, normalizeValue = false) => {
    setDrops((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: normalizeValue ? normalizeTime(value) : value };
      return next;
    });
  };

  const origin = pickups[0];
  const destination = drops[0];
  const hasRouteContext = Boolean(origin.city && origin.state && destination.city && destination.state);
  const transitPlaceholder = origin.date && destination.date ? 'Auto-calculating' : 'Pending schedule';

  return (
    <div className={drawerStyles.backdrop}>
      <div className={drawerStyles.drawer}>
        <div className={drawerStyles.header}>
          <div>
            <h2>Run a quote</h2>
            <p>Capture the lane, accessorials, and notes you need. DAT RateView will populate as soon as integrations are live.</p>
          </div>
          <button type="button" className={drawerStyles.closeButton} onClick={onClose} aria-label="Close quote drawer">
            <FiX />
          </button>
        </div>
        <div className={drawerStyles.content}>
          <div className={styles.layout}>
            <form id="quote-form" onSubmit={handleSubmit} className={styles.formColumn}>
              <section className={styles.section}>
                <h4>Shipment profile</h4>
                <div className={styles.segmentedControl}>
                  {modes.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={[styles.segment, mode === value ? styles.active : ''].filter(Boolean).join(' ')}
                      onClick={() => setMode(value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <label className={styles.label}>
                  Equipment requirement
                  <select value={equipment} onChange={(event) => setEquipment(event.target.value)}>
                    {availableEquipment.map((equip) => (
                      <option key={equip} value={equip}>
                        {equip}
                      </option>
                    ))}
                  </select>
                </label>
                {trailerDefaults[equipment] && (
                  <div className={styles.metaChips}>
                    <span>Preferred lengths:</span>
                    <div className={styles.chipRow}>
                      {trailerDefaults[equipment].lengths.map((length) => (
                        <span key={length} className={styles.chip}>
                          {length}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className={styles.tip}>
                  <FiInfo />
                  <p>Premium insights like market rates and lane intelligence will automatically attach once the backend integrations are configured.</p>
                </div>
              </section>

              <section className={styles.section}>
                <h4>Stops</h4>
                <div className={styles.stopSummary}>
                  <label>
                    Pickups
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={pickupCount}
                      onChange={(event) => setPickupCount(Number(event.target.value))}
                    />
                  </label>
                  <label>
                    Deliveries
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={dropCount}
                      onChange={(event) => setDropCount(Number(event.target.value))}
                    />
                  </label>
                </div>
                <div className={styles.stopGrid}>
                  {pickups.map((pickup, index) => (
                    <div key={`pickup-${index}`} className={styles.stopCard}>
                      <span className={styles.stopBadge}>Pickup {index + 1}</span>
                      <div className={styles.inlineFields}>
                        <label>
                          City
                          <input value={pickup.city} onChange={(event) => handlePickupChange(index, 'city', event.target.value)} />
                        </label>
                        <label>
                          State
                          <input value={pickup.state} onChange={(event) => handlePickupChange(index, 'state', event.target.value)} />
                        </label>
                        <label>
                          Zip
                          <input value={pickup.zip} onChange={(event) => handlePickupChange(index, 'zip', event.target.value)} />
                        </label>
                      </div>
                      <div className={styles.inlineFields}>
                        <label>
                          Pickup date
                          <div className={styles.inputWithIcon}>
                            <FiCalendar />
                            <input
                              type="date"
                              value={pickup.date}
                              onChange={(event) => handlePickupChange(index, 'date', event.target.value)}
                            />
                          </div>
                        </label>
                        <label>
                          Time
                          <input
                            value={pickup.time}
                            list={timeListId}
                            onChange={(event) => handlePickupChange(index, 'time', event.target.value)}
                            onBlur={(event) => handlePickupChange(index, 'time', event.target.value, true)}
                            placeholder="08:00"
                          />
                        </label>
                      </div>
                      <label>
                        Notes
                        <textarea
                          rows={2}
                          value={pickup.notes}
                          onChange={(event) => handlePickupChange(index, 'notes', event.target.value)}
                          placeholder="Dock instructions, reference numbers, appointment details"
                        />
                      </label>
                    </div>
                  ))}
                </div>
                <div className={styles.stopGrid}>
                  {drops.map((drop, index) => (
                    <div key={`drop-${index}`} className={styles.stopCard}>
                      <span className={styles.stopBadge}>Delivery {index + 1}</span>
                      <div className={styles.inlineFields}>
                        <label>
                          City
                          <input value={drop.city} onChange={(event) => handleDropChange(index, 'city', event.target.value)} />
                        </label>
                        <label>
                          State
                          <input value={drop.state} onChange={(event) => handleDropChange(index, 'state', event.target.value)} />
                        </label>
                        <label>
                          Zip
                          <input value={drop.zip} onChange={(event) => handleDropChange(index, 'zip', event.target.value)} />
                        </label>
                      </div>
                      <div className={styles.inlineFields}>
                        <label>
                          Delivery date
                          <div className={styles.inputWithIcon}>
                            <FiCalendar />
                            <input
                              type="date"
                              value={drop.date}
                              onChange={(event) => handleDropChange(index, 'date', event.target.value)}
                            />
                          </div>
                        </label>
                        <label>
                          Time
                          <input
                            value={drop.time}
                            list={timeListId}
                            onChange={(event) => handleDropChange(index, 'time', event.target.value)}
                            onBlur={(event) => handleDropChange(index, 'time', event.target.value, true)}
                            placeholder="15:00"
                          />
                        </label>
                      </div>
                      <label>
                        Notes
                        <textarea
                          rows={2}
                          value={drop.notes}
                          onChange={(event) => handleDropChange(index, 'notes', event.target.value)}
                          placeholder="Appointment numbers, contact expectations"
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </section>

              <section className={styles.section}>
                <h4>Additional details</h4>
                <label className={styles.label}>
                  Market notes
                  <textarea
                    rows={4}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Share context like secured carrier pricing, customer expectations, or margin guardrails."
                  />
                </label>
                <div className={styles.promise}>
                  <FiMapPin />
                  <p>Quotes generated here default to FreightLogic as the managing carrier. Savings from awarded tenders pass directly to you—no hidden margins.</p>
                </div>
              </section>
            </form>

            <aside className={styles.visualColumn}>
              <div className={styles.visualCard}>
                <div className={styles.visualHeader}>
                  <div>
                    <h4>Route visualization</h4>
                    <p>Preview will render as soon as origin and destination are defined.</p>
                  </div>
                  <FiMapPin />
                </div>
                <div className={styles.visualBody}>
                  {hasRouteContext ? (
                    <div className={styles.routeSummary}>
                      <span>{origin.city}, {origin.state}</span>
                      <span className={styles.routeArrow}>→</span>
                      <span>{destination.city}, {destination.state}</span>
                    </div>
                  ) : (
                    <div className={styles.visualEmpty}>Add at least one pickup and delivery to preview the lane.</div>
                  )}
                  <div className={styles.visualPlaceholder}>
                    <span>Route preview placeholder</span>
                  </div>
                </div>
                <footer className={styles.visualFooter}>
                  Estimated transit times and Google heatmaps will appear here once DAT RateView and Places integrations are activated.
                </footer>
              </div>

              <div className={styles.insightsCard}>
                <div className={styles.insightsHeader}>
                  <FiTrendingUp />
                  <div>
                    <h4>Market intelligence</h4>
                    <p>In-flight signals to accompany your quote.</p>
                  </div>
                </div>
                <div className={styles.insightsGrid}>
                  <div className={styles.insightTile}>
                    <span className={styles.tileLabel}>Fuel index</span>
                    <strong className={styles.tileValue}>$4.32</strong>
                    <span className={styles.tileHint}>Weekly diesel average</span>
                  </div>
                  <div className={styles.insightTile}>
                    <span className={styles.tileLabel}>Transit ETA</span>
                    <strong className={styles.tileValue}>{transitPlaceholder}</strong>
                    <span className={styles.tileHint}>Auto-calculated with DAT + Places</span>
                  </div>
                  <div className={styles.insightTile}>
                    <span className={styles.tileLabel}>Market heatmap</span>
                    <div className={styles.heatPlaceholder}>DAT Heat index coming soon</div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
        <div className={drawerStyles.footer}>
          <span className={styles.footerCopy}>Submitting will request pricing instantly once DAT credentials are connected.</span>
          <Button type="submit" form="quote-form" variant="primary">
            Submit quote
          </Button>
        </div>
        <datalist id={timeListId}>
          {timeOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      </div>
    </div>
  );
};

CreateQuoteDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired
};

CreateQuoteDrawer.defaultProps = {
  open: false
};

export default CreateQuoteDrawer;
